import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getMetaList } from "../../api/design/meta";
import MobileBoardPagination from "../../components/common/board/MobileBoardPagination";
import { formatDate } from "../../utils/date";

const LABELS = {
  FCR: "설계변경요청",
  FCN: "설계변경통보",
  DCN: "설계변경도면",
};

export default function FcrList() {
  const navigate = useNavigate();
  const { id: project, docType } = useParams(); // docType: 'all' | 'fcr' | 'fcn' | 'dcn' 예상

  // URL 파라미터 정규화
  const normalizedDocType = (docType || "all").toUpperCase(); // ALL / FCR / FCN / DCN

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  // 페이지네이션
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  // 검색/필터
  const [q, setQ] = useState("");
  const [searchField, setSearchField] = useState("title");
  const [dateFilter, setDateFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchTrigger, setSearchTrigger] = useState(0);

  const [loading, setLoading] = useState(false);
  const [expandedIds, setExpandedIds] = useState(new Set());

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        project,
        [searchField]: q,
        start_date: startDate,
        end_date: endDate,
        page,
        page_size: pageSize,
      };

      // ALL 이 아니면 doc_type 필터 추가
      if (normalizedDocType !== "ALL") {
        params.doc_type = normalizedDocType;
      }

      const data = await getMetaList(params);

      setRows(data.results || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error("FCR 목록 로드 실패:", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [
    project,
    normalizedDocType,
    searchField,
    q,
    startDate,
    endDate,
    page,
    pageSize,
  ]);

  // 최초 로드 + 필터 변경 시
  useEffect(() => {
    if (project) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, normalizedDocType, page, pageSize, searchTrigger]);

  const applySearch = () => {
    setPage(1);
    setSearchTrigger((v) => v + 1);
  };

  const toggleExpand = (dns) => {
    const newExpanded = new Set(expandedIds);
    if (newExpanded.has(dns)) {
      newExpanded.delete(dns);
    } else {
      newExpanded.add(dns);
    }
    setExpandedIds(newExpanded);
  };

  const getDeptDisplay = (row) => {
    const t = row.doc_type;
    if (t === "FCR") return row.meta?.requestDept || "-";
    if (t === "FCN") {
      const list = row.meta?.requestDeptList || [];
      return list.length
        ? list.map((d) => `${d.company}(${d.name})`).join(", ")
        : "-";
    }
    if (t === "DCN") return row.meta?.issueDept || "-";
    return "-";
  };

  const renderDocCard = (row, level = 0) => {
    const hasChildren = row.related_docs && row.related_docs.length > 0;
    const isExpanded = expandedIds.has(row.doc_dns);
    const indent = level > 0 ? Math.min(level * 8, 16) : 0;

    return (
      <div key={row.doc_dns} className="w-full">
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (hasChildren) {
              toggleExpand(row.doc_dns);
            } else {
              navigate(
                `/project/${project}/design/${row.doc_type?.toLowerCase()}/${row.doc_dns}/view`
              );
            }
          }}
          className={`w-full text-left rounded-lg border p-2 transition-all
            ${level === 0
              ? "bg-white border-gray-200 hover:border-blue-300"
              : "bg-gray-50 border-gray-100 hover:bg-gray-100"
            }
            active:bg-gray-50`}
          style={{
            marginLeft: indent,
            width: `calc(100% - ${indent}px)`,
          }}
        >
          <div className="flex items-start gap-1.5">
            {/* 확장 버튼 */}
            {hasChildren && (
              <span className="text-gray-400 text-[10px] mt-0.5 flex-shrink-0">
                {isExpanded ? "▼" : "▶"}
              </span>
            )}

            <div className="flex-1 min-w-0">
              {/* 상단: 타입 + 제목 + 상태 */}
              <div className="flex items-center gap-1 mb-1">
                <span className="text-[9px] font-medium text-gray-500 px-1 py-px bg-gray-100 rounded flex-shrink-0">
                  {row.doc_type}
                </span>
                {row.state && (
                  <span className="text-[9px] font-medium text-blue-600 px-1 py-px bg-blue-50 rounded flex-shrink-0">
                    {row.state}
                  </span>
                )}
              </div>

              {/* 제목 */}
              <h3 className="text-[13px] font-medium text-gray-900 line-clamp-2 mb-1 leading-snug break-words">
                {row.title || "제목 없음"}
              </h3>

              {/* 부서 (있을 경우) */}
              {getDeptDisplay(row) !== "-" && (
                <p className="text-[11px] text-gray-500 truncate mb-0.5">
                  {getDeptDisplay(row)}
                </p>
              )}

              {/* 하단: 작성자 + 날짜 */}
              <div className="flex items-center gap-1 text-[11px] text-gray-400">
                <span className="truncate">{row.created_by?.name || "-"}</span>
                <span>·</span>
                <span className="flex-shrink-0">{row.created_at ? formatDate(row.created_at) : "-"}</span>
              </div>
            </div>
          </div>
        </button>

        {hasChildren && isExpanded && (
          <div className="mt-1 space-y-1">
            {row.related_docs.map((child) => renderDocCard(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  // 제목 문구
  const currentTitle =
    normalizedDocType === "ALL"
      ? "설계변경 문서 목록"
      : `${LABELS[normalizedDocType] || "설계변경"} 문서 목록`;

  // 새 문서 등록 시 사용할 타입 (ALL이면 기본 FCR로)
  const createDocType =
    normalizedDocType === "ALL" ? "fcr" : normalizedDocType.toLowerCase(); // fcr / fcn / dcn

  return (
    <div className="mobile-page-root">
      <div className="mobile-page-root-inner space-y-3 mobile-bottom-spacing">
        {/* 헤더 */}
        <div className="bg-white rounded-lg p-3 border">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="min-w-0 flex-1">
              <h2 className="text-base font-bold text-gray-900 truncate">
                {currentTitle}
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                {docType?.toUpperCase()} 목록
              </p>
            </div>
            <button
              onClick={() =>
                navigate(`/project/${project}/design/${createDocType}/new`)
              }
              className="btn-create flex-shrink-0 text-xs"
            >
              등록
            </button>
          </div>
        </div>

        {/* 검색창 */}
        <div className="bg-white rounded-lg p-3 border">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="제목 검색..."
              className="flex-1 min-w-0 text-sm border border-gray-200 rounded-lg px-3 py-2"
              onKeyDown={(e) => e.key === "Enter" && applySearch()}
            />
            <button
              onClick={applySearch}
              className="btn-search flex-shrink-0 text-xs"
            >
              검색
            </button>
          </div>
        </div>

        {/* 문서 리스트 */}
        <div className="space-y-2">
          {loading ? (
            <>
              <div className="bg-white rounded-lg p-3 border animate-pulse h-24" />
              <div className="bg-white rounded-lg p-3 border animate-pulse h-24" />
              <div className="bg-white rounded-lg p-3 border animate-pulse h-24" />
            </>
          ) : rows.length === 0 ? (
            <div className="bg-white rounded-lg p-6 text-center border">
              <p className="text-sm text-gray-500">
                {q ? "검색 결과가 없습니다." : "문서가 없습니다."}
              </p>
              <button
                onClick={() =>
                  navigate(`/project/${project}/design/${createDocType}/new`)
                }
                className="btn-create mt-4"
              >
                새 문서 등록
              </button>
            </div>
          ) : (
            rows.map((row) => renderDocCard(row, 0))
          )}
        </div>

        {/* 페이지네이션 */}
        {!loading && rows.length > 0 && (
          <div className="bg-white rounded-lg p-3 border">
            <MobileBoardPagination
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={setPage}
            />
          </div>
        )}
      </div>
    </div>
  );
}

